// ---------------------------------------------------------------------------
// 冪等な読み取りリクエストだけを対象にした再試行ポリシー
// ---------------------------------------------------------------------------
// 経緯:
//   デザインエディタの一覧 API (/page/list, /template/list,
//   /template/layout/list, /template/block/list) が間欠的に HTTP 500
//   ({"status":"SAVER_ERROR","error":{"E000008":"api saver error"}}) を返す。
//
//   後端の原因はインデックス MySQL のコネクションプールに残った「サーバ側で
//   切られた接続」。go-sql-driver がそれを使うと ErrInvalidConn
//   ("invalid connection") を返し、これは driver.ErrBadConn ではないため
//   database/sql は透過的な再試行をしてくれず、一覧取得がそのまま失敗する。
//   本命の修正は cms 側 (pkg/index/entity_index/conn.go) のプール寿命設定。
//   ここはその多重防御 — 残る競合 (プールから渡された直後に切られる等) で
//   画面が空になるのを防ぐ。
//
// ⚠ **保存系は構造的に再試行できないようにしてある。**
//   /form/items/update や各設定保存は「全置換」なので、再送は二重作成や
//   意図しない上書きになる。応答が返らなかった場合は「後端に届いて処理まで
//   済んだが応答だけ失われた」のか区別が付かないため、副作用のある POST は
//   一律で 1 回しか投げない。
//
//   **URL パターンで「保存か読み取りか」を見分ける方式は採らない。**
//   一致条件の書き間違いや、後から足されたエンドポイントの巻き込みで
//   保存が再送される事故が起きうる。判定は呼び出し側が
//   cmsIdempotentRead で明示的に申告する方式にしてある
//   (cms-axios.js の httpPost は付けない / httpPostRead だけが付ける)。
//   つまり既定は「再試行しない」で、外し忘れではなく付け忘れの側に倒れる。
//
// ⚠ **401 / 403 は再試行しない。** 認証切れは待っても直らず、
//   ログイン画面への遷移を遅らせるだけになる。
//
// ⚠ **GET も既定では再試行しない**（理由は isRetryableRequest のコメント）。
//   再試行するのは httpGetRead / httpPostRead で申告した一覧取得だけ。

export const DEFAULT_MAX_RETRIES = 2;
export const DEFAULT_BASE_DELAY = 300;
export const DEFAULT_MAX_DELAY = 2000;

// 冪等なリクエストか。判定は cmsIdempotentRead の申告だけで、
// メソッドも URL も見ない。
//
// ⚠ **GET も既定では再試行しない。**
//   GET は HTTP の定義上冪等だが、「再送しても壊れない」ことと
//   「再送すべき」ことは別。実際に /user と /config-api/site-admin-config が
//   間欠的に 504 を返しており、GET を一律で再試行するとこの 2 本が
//   504 の最中に 3 倍に増える。504 は後端が既に詰まっている合図なので、
//   そこへ投げ足すのは症状を悪化させる方向にしか働かない。
//   デザインエディタは起動時に一覧を並列で 5 本投げるため、
//   一律再試行の最悪ケースは全体で 3 倍になる。
//
//   よって対象は「間欠 500 で画面が空になるのを防ぎたい一覧取得」だけに絞り、
//   呼び出し側が httpGetRead / httpPostRead で明示的に申告する。
export function isRetryableRequest(config) {
  if (!config) return false;
  return config.cmsIdempotentRead === true;
}

// 再試行して意味のある失敗か。
//
// 対象は「後端の一時的な失敗」だけに絞る。5xx を一律で対象にはしない。
//
// - 応答なし (接続断・タイムアウト) → 再試行する
// - 500 / 502 / 503 → 再試行する。観測された SAVER_ERROR は 500。
// - **504 は再試行しない。** Gateway Timeout は「後端が時間内に返せなかった」
//   合図で、投げ直しても同じか悪化する。詰まっている相手に負荷を足すだけ。
// - **429 も再試行しない。** ここのバックオフ (300ms〜1.2s) は
//   レート制限に対しては短すぎて、無意味に叩き直すことになる。
// - 401 / 403 やその他の 4xx → 再試行しない。待っても直らない。
// - 中断 (AbortController / CancelToken) → 再試行しない
export const RETRYABLE_STATUSES = [500, 502, 503];

export function isRetryableError(error) {
  if (!error) return false;
  // axios のキャンセル
  if (error.__CANCEL__ || error.code === 'ERR_CANCELED') return false;

  const status = error.response?.status;
  if (status === undefined || status === null) {
    // 応答が返っていない = 接続断・タイムアウト
    return true;
  }
  return RETRYABLE_STATUSES.includes(status);
}

// 指数バックオフ + ジッタ。
// デザインエディタは起動時に一覧 API を並列で数本投げるため、ジッタなしだと
// 全部が同じ時刻に再突入して同じ失敗を繰り返す。
// base * 2^attempt を上限で切り、その半分〜全量の範囲でばらす。
export function backoffDelay(attempt, options = {}) {
  const {
    baseDelay = DEFAULT_BASE_DELAY,
    maxDelay = DEFAULT_MAX_DELAY,
    random = Math.random,
  } = options;

  const capped = Math.min(maxDelay, baseDelay * 2 ** attempt);
  return Math.round(capped / 2 + random() * (capped / 2));
}

const defaultSleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// ---------------------------------------------------------------------------
// 再試行つきリクエスト実行
// ---------------------------------------------------------------------------
// request: (config) => Promise<response>  (インターセプタを通さない生の送信)
// 上限まで失敗したら最後の error をそのまま throw する。呼び出し側は
// これまでと同じ値を受け取るので、表示側の扱いは変わらない。
export async function requestWithRetry(request, config, options = {}) {
  const {
    maxRetries = DEFAULT_MAX_RETRIES,
    baseDelay = DEFAULT_BASE_DELAY,
    maxDelay = DEFAULT_MAX_DELAY,
    random = Math.random,
    sleep = defaultSleep,
    onRetry,
  } = options;

  const retryable = isRetryableRequest(config);

  for (let attempt = 0; ; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop -- 再試行は直列でなければ意味がない
      return await request(config);
    } catch (error) {
      const canRetry = retryable
        && attempt < maxRetries
        && isRetryableError(error);

      if (!canRetry) throw error;

      const delay = backoffDelay(attempt, { baseDelay, maxDelay, random });
      if (onRetry) {
        onRetry({
          url: config?.url,
          method: config?.method,
          attempt: attempt + 1,
          maxRetries,
          delay,
          error,
        });
      }
      // eslint-disable-next-line no-await-in-loop -- 同上
      await sleep(delay);
    }
  }
}
